import { PropsWithChildren, ReactNode, useEffect, useState } from "react";

import { classNames } from "@helpers/css";

// Whether a scrolling area has content cut off above or below it. Updates as
// it scrolls, and as it or its content changes size.
const useCutOffEdges = () => {
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  const [content, setContent] = useState<HTMLDivElement | null>(null);
  const [edges, setEdges] = useState({ top: false, bottom: false });

  useEffect(() => {
    if (!scroller || !content) {
      return;
    }
    const update = () =>
      setEdges({
        top: scroller.scrollTop > 0,
        // A pixel of slack, as scroll positions can be fractional.
        bottom:
          scroller.scrollTop + scroller.clientHeight <
          scroller.scrollHeight - 1,
      });
    update();
    scroller.addEventListener("scroll", update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(scroller);
    observer.observe(content);
    return () => {
      scroller.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, [scroller, content]);

  return { setScroller, setContent, ...edges };
};

const ComplianceProgramFormStep = ({
  title,
  actions,
  className,
  scrollable = false,
  children,
}: PropsWithChildren<{
  title: string;
  className?: string;
  // On a wide Questions tab, keeps the title row in place and scrolls what
  // is under it. The section needs a height for this, from its container.
  scrollable?: boolean;
  // Sits on the title's line, at the right.
  actions?: ReactNode;
}>) => {
  const { setScroller, setContent, top, bottom } = useCutOffEdges();
  return (
    <section className={classNames("flex flex-col gap-3", className)}>
      <div className="flex min-h-8 flex-wrap items-center justify-between gap-3">
        <h2 className="tf-header-lg-dark">{title}</h2>
        {actions}
      </div>
      {scrollable ? (
        // Rounded, so content leaving the area curves away rather than being
        // cut straight across, with a line on each edge that has more beyond
        // it. The lines are always there, so showing one moves nothing.
        // Relative, so absolute content such as a toggle's screen-reader
        // label is clipped here too. Otherwise it makes the page, which does
        // not scroll, taller than its box, and scrolling an error into view
        // moves the whole tab up.
        <div
          ref={setScroller}
          className={classNames(
            "rounded-lg border-y @[1200px]/layout-main:relative @[1200px]/layout-main:min-h-0 @[1200px]/layout-main:flex-1 @[1200px]/layout-main:overflow-y-auto",
            top ? "border-t-gray-200" : "border-t-transparent",
            bottom ? "border-b-gray-200" : "border-b-transparent",
          )}
        >
          <div ref={setContent} className="flex flex-col gap-3">
            {children}
          </div>
        </div>
      ) : (
        children
      )}
    </section>
  );
};

export default ComplianceProgramFormStep;
